import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

/**
 * Configuration de routing principale de l'application
 * Architecture avec préfixes de modules pour une meilleure organisation
 * Tous les modules sont chargés en lazy loading pour optimiser les performances
 */
const routes: Routes = [
  // ==================== AUTHENTICATION MODULE ====================
  {
    path: 'auth',
    loadChildren: () => import('./login/auth.module').then(m => m.AuthModule)
  },
  // Routes d'authentification sans préfixe (pour compatibilité avec l'ancien système)
  {
    path: 'login',
    redirectTo: 'auth/login',
    pathMatch: 'full'
  },
  {
    path: 'register',
    redirectTo: 'auth/register',
    pathMatch: 'full'
  },
  {
    path: 'register-user',
    redirectTo: 'auth/register-user',
    pathMatch: 'full'
  },
  {
    path: 'reset-password',
    redirectTo: 'auth/reset-password',
    pathMatch: 'full'
  },
  {
    path: 'reset-password-user',
    redirectTo: 'auth/reset-password-user',
    pathMatch: 'full'
  },
  {
    path: 'email-verification',
    redirectTo: 'auth/email-verification',
    pathMatch: 'full'
  },

  // ==================== DASHBOARD MODULE ====================
  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.module').then(m => m.DashboardModule)
  },

  // ==================== FAMILLE MODULE ====================
  {
    path: 'famille',
    loadChildren: () => import('./familleManagment/famille.module').then(m => m.FamilleModule)
  },
  // Routes de compatibilité (redirection vers le module famille)
  { path: 'list-famille', redirectTo: 'famille/list', pathMatch: 'full' },
  { path: 'create-famille', redirectTo: 'famille/create', pathMatch: 'full' },
  { path: 'edit-famille/:familyID', redirectTo: 'famille/edit/:familyID' },
  { path: 'list-archived', redirectTo: 'famille/archived', pathMatch: 'full' },
  { path: 'list-payement-chef-famille', redirectTo: 'famille/payements', pathMatch: 'full' },
  { path: 'list-payement-chef-famille/:familyID', redirectTo: 'famille/payements/:familyID' },

  // ==================== PAYMENT MODULE ====================
  {
    path: 'payment',
    loadChildren: () => import('./payement/payment.module').then(m => m.PaymentModule)
  },
  // Routes de compatibilité (redirection vers le module payment)
  { path: 'list-paiement', redirectTo: 'payment/list', pathMatch: 'full' },
  { path: 'edit-payement/:id', redirectTo: 'payment/edit/:id' },
  { path: 'nouveau-payement/:id', redirectTo: 'payment/nouveau/:id' },
  { path: 'apercu-payement/:id', redirectTo: 'payment/apercu/:id' },
  { path: 'list-payement-inscrit/:id', redirectTo: 'payment/list-inscrit/:id' },
  { path: 'list-payement-per', redirectTo: 'payment/list-personnel', pathMatch: 'full' },
  { path: 'edit-payement-per/:id', redirectTo: 'payment/edit-personnel/:id' },
  { path: 'apercu-payement-per/:id', redirectTo: 'payment/apercu-personnel/:id' },
  { path: 'nouveau-payement-per/:id', redirectTo: 'payment/nouveau-personnel/:id' },
  { path: 'list-payement-par-personnel/:id', redirectTo: 'payment/by-personnel/:id' },

  // ==================== USER MODULE (Espace Client) ====================
  {
    path: 'user',
    loadChildren: () => import('./user/user.module').then(m => m.UserModule)
  },
  // Routes de compatibilité (redirection vers le module user)
  { path: 'chat', redirectTo: 'user/chat', pathMatch: 'full' },
  { path: 'kafala-form', redirectTo: 'user/kafala-form', pathMatch: 'full' },
  { path: 'subject', redirectTo: 'user/subject', pathMatch: 'full' },
  { path: 'paiment-en-ligne', redirectTo: 'user/paiment-en-ligne', pathMatch: 'full' },
  { path: 'kafala-selector', redirectTo: 'user/kafala-selector', pathMatch: 'full' },
  { path: 'success', redirectTo: 'user/success', pathMatch: 'full' },
  { path: 'fail', redirectTo: 'user/fail', pathMatch: 'full' },
  { path: 'profil-user', redirectTo: 'user/profil', pathMatch: 'full' },
  { path: 'profil-user/:id', redirectTo: 'user/profil/:id' },
  { path: 'donations', redirectTo: 'user/donations', pathMatch: 'full' },

  // ==================== MANAGEMENT MODULE ====================
  {
    path: 'management',
    loadChildren: () => import('./other.module').then(m => m.OtherModule)
  },
  
  // Routes de compatibilité (redirection vers le module management)
  // Inscrits
  // { path: 'accueil', redirectTo: 'management/accueil', pathMatch: 'full' },
  { path: 'inscrit', redirectTo: 'management/inscrit', pathMatch: 'full' },
  { path: 'inscrit/:familyDefID', redirectTo: 'management/inscrit/:familyDefID' },
  { path: 'edit-member/:familyDefID', redirectTo: 'management/edit-member/:familyDefID' },
  { path: 'edit-inscrit/:id', redirectTo: 'management/edit-inscrit/:id' },
  { path: 'apercu-inscrit/:id', redirectTo: 'management/apercu-inscrit/:id' },
  { path: 'desactive-inscrit', redirectTo: 'management/desactive-inscrit', pathMatch: 'full' },
  { path: 'inscrit-archived', redirectTo: 'management/inscrit-archived', pathMatch: 'full' },
  
  // Personnel
  { path: 'personnel', redirectTo: 'management/personnel', pathMatch: 'full' },
  { path: 'nouveau-personnel/:id', redirectTo: 'management/nouveau-personnel/:id' },
  { path: 'personel-archived', redirectTo: 'management/personel-archived', pathMatch: 'full' },
  
  // Paiements
  { path: 'paiement-inscrit', redirectTo: 'management/paiement-inscrit', pathMatch: 'full' },
  { path: 'due-inscrit', redirectTo: 'management/due-inscrit', pathMatch: 'full' },
  { path: 'recu/:id', redirectTo: 'management/recu/:id' },
  { path: 'recu-personnel/:id', redirectTo: 'management/recu-personnel/:id' },
  
  // Admin
  { path: 'edit-admin/:id', redirectTo: 'management/edit-admin/:id' },
  
  // Factures
  { path: 'list-facture', redirectTo: 'management/list-facture', pathMatch: 'full' },
  { path: 'nouveau-facture', redirectTo: 'management/nouveau-facture', pathMatch: 'full' },
  
  // Mouvements
  { path: 'list-mouvement', redirectTo: 'management/list-mouvement', pathMatch: 'full' },
  { path: 'list-entree', redirectTo: 'management/list-entree', pathMatch: 'full' },
  { path: 'list-sortie', redirectTo: 'management/list-sortie', pathMatch: 'full' },
  
  // Matières
  { path: 'list-matiere', redirectTo: 'management/list-matiere', pathMatch: 'full' },
  { path: 'nouvelle-matiere', redirectTo: 'management/nouvelle-matiere', pathMatch: 'full' },
  { path: 'detail-matiere/:id', redirectTo: 'management/detail-matiere/:id' },
  { path: 'edit-matiere/:id', redirectTo: 'management/edit-matiere/:id' },
  
  // Établissements
  { path: 'list-etablissement', redirectTo: 'management/list-etablissement', pathMatch: 'full' },
  { path: 'etablissement/:id', redirectTo: 'management/etablissement/:id' },
  
  // Users (admin)
  { path: 'list-user', redirectTo: 'management/list-user', pathMatch: 'full' },
  { path: 'user/:id', redirectTo: 'management/user/:id' },
  
  // Kafala
  { path: 'kafala-expiration', redirectTo: 'management/kafala-expiration', pathMatch: 'full' },

  // ==================== REDIRECTIONS ====================
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '/login'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    enableTracing: false, // Mettre à true pour déboguer le routing
    useHash: false
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
